import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inr, fmtDate, errMsg } from "../../lib/format";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Search, Plus, ChevronRight } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", upi_id: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", upi_id: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(
    (u) => u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()) || u.phone?.includes(search)
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline">Management</p>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Users</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="rounded-sm h-10 gap-2"><Plus size={16} /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-heading">Create User</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label className="overline">Full Name</Label><Input data-testid="new-user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
              <div><Label className="overline">Email</Label><Input data-testid="new-user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
              <div><Label className="overline">Password</Label><Input data-testid="new-user-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline">Phone</Label><Input data-testid="new-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-sm h-11" /></div>
                <div><Label className="overline">UPI ID</Label><Input data-testid="new-user-upi" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="mt-2 rounded-sm h-11 font-mono-num" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="new-user-submit" type="submit" disabled={saving} className="rounded-sm w-full h-11">{saving ? "Creating…" : "Create User"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users" className="pl-9 rounded-sm h-10" />
      </div>

      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold hidden sm:table-cell">Email</th>
              <th className="px-5 py-3 font-semibold">Balance</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold hidden md:table-cell">Created</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="px-5 py-6 text-muted-foreground">No users found.</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}`)} className="border-b border-border/40 last:border-0 hover:bg-white/5 cursor-pointer">
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3 font-mono-num text-xs hidden sm:table-cell">{u.email}</td>
                <td className="px-5 py-3 font-mono-num font-medium">{inr(u.balance)}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold uppercase ${u.status === "active" ? "text-[#059669]" : "text-[#DC2626]"}`}>{u.status}</span>
                </td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(u.created_at)}</td>
                <td className="px-5 py-3 text-right"><ChevronRight size={16} className="text-muted-foreground inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
